/**
 * format-prefs.ts — Central home for FormatPreferences type and DEFAULT_FORMAT.
 *
 * Extracted from ActivitySheet.tsx so activity-engine.ts can import the type
 * without creating a circular dependency.
 */

export interface FormatPreferences {
  fontFamily: 'sans' | 'serif';
  /** Base body font size in px */
  fontSize: 13 | 15 | 17 | 19;
  lineSpacing: 'compact' | 'normal' | 'relaxed';
  questionSpacing: 'tight' | 'normal' | 'generous';
  workspaceSize: 'none' | 'small' | 'medium' | 'large' | 'xl';
  numberingStyle: '123' | 'ABC' | 'roman' | 'bullet' | 'checkbox';
  orientation: 'portrait' | 'landscape';
  /**
   * Visual treatment density for diagrams/illustrations — prompt hint only.
   * Never authorizes new problems or scenarios.
   */
  graphicsDensity: 'none' | 'minimal' | 'standard' | 'rich';
  /** Page number format shown in printed output. */
  pageNumberStyle: 'none' | 'page-x' | 'page-x-of-y';
  /** Number of ruled answer lines rendered per problem (lines workspace). Default 3. */
  answerLinesCount: number;
  /** Height in px of paragraph / computation response boxes. Default 80. */
  responseBoxHeight: number;
  /** Overlay a graph-paper grid on every workspace area. Default false. */
  graphPaperAreas: boolean;
  /** Extra diagram / drawing space (px) appended below each problem. 0 = none. Default 0. */
  diagramSpaceHeight: number;
  /** Insert a page break after every section when printing. Default false. */
  manualPageBreaks: boolean;
  /**
   * Text color preset for the document body.
   * Applied to question text and section instructions throughout the sheet.
   */
  fontColor: 'slate' | 'navy' | 'black' | 'forest';
}

/**
 * NON-NEGOTIABLE PRODUCT RULE — Activity Sheet Workspace
 * ─────────────────────────────────────────────────────────
 * Every generated activity sheet MUST present questions as clearly numbered
 * vertical items with generous student workspace between each item:
 *   • BOX  — for drawings, sketches, diagrams, graphs, math/computational work
 *   • LINES — for short written responses (sentences or short paragraphs)
 *
 * These defaults are SET TO ENFORCE this rule. Do NOT lower workspaceSize,
 * answerLinesCount, or responseBoxHeight below these values in the generated
 * default. Teachers may adjust after generation, but the sheet must arrive
 * in a compliant state. Workspace kind is auto-detected per problem by
 * detectWorkspaceKind() and tagged by tagProblems() — both are mandatory.
 */
export const DEFAULT_FORMAT: FormatPreferences = {
  fontFamily: 'sans',
  fontSize: 15,
  lineSpacing: 'normal',
  questionSpacing: 'generous',   // enforces clear vertical separation between numbered items
  workspaceSize: 'large',        // non-negotiable minimum — generous workspace for every problem
  numberingStyle: '123',
  orientation: 'portrait',
  graphicsDensity: 'none',
  pageNumberStyle: 'none',
  answerLinesCount: 5,           // 5 ruled lines ≈ 160 px of writing space — generous for sentences/paragraphs
  responseBoxHeight: 144,        // px — generous box for mathematical work and calculations
  graphPaperAreas: false,
  diagramSpaceHeight: 0,
  manualPageBreaks: false,
  fontColor: 'slate',
};
